import { inject, Injectable } from '@angular/core';
import type { FormArray } from '@angular/forms';
import { FormControl } from '@angular/forms';

import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { of, Subject, take } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';
import { FeatureDiscoveryActions } from '@core/feature-discovery-data';
import { I18nService } from '@core/i18n-legacy';
import { DialogService, DialogWidth } from '@core/user-interface-legacy';

import { ConfirmUnassignLocationDialogComponent } from '../dialogs/confirm-unassign-location-dialog/confirm-unassign-location-dialog.component';
import type { UnassignLocationDialogResult } from '../dialogs/confirm-unassign-location-dialog/confirm-unassign-location-dialog.types';

@Injectable()
export abstract class UnassignLocationFacade {
  private readonly dialogService = inject(DialogService);
  private readonly i18n = inject(I18nService);
  private readonly store = inject(Store);

  public ngOnDestroy$ = new Subject<void>();

  private locationIds: number[];

  public execute(locationId: number, unassingNeedsConfirm?: boolean) {
    this.locationIds = this.locationsControl.value || [];
    this.locationsControl.markAsTouched();

    this.confirmUnassignLocationIfNeeded$(unassingNeedsConfirm)
      .pipe(take(1), filterBoolean)
      .subscribe(() => {
        this.setLocationsControl(locationId);
        if (this.validateLocations) this.validateLocations();
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public abstract get locationsControl(): FormArray<FormControl>;
  public abstract validateLocations?(): void;

  private setLocationsControl(locationId: number) {
    const newLocationIds = this.locationIds.filter(id => id !== locationId);

    this.locationsControl.clear({ emitEvent: false });
    newLocationIds.forEach(id => {
      this.locationsControl.push(new FormControl(id), { emitEvent: false });
    });
    this.locationsControl.markAsDirty();
    this.locationsControl.updateValueAndValidity();
  }

  private confirmUnassignLocationIfNeeded$(unassingNeedsConfirm?: boolean) {
    return unassingNeedsConfirm
      ? this.openConfirmUnassignLocationDialog$().pipe(
          tap(result => {
            if (result.confirmed && result.payload.dontShowAgain) {
              this.store.dispatch(
                FeatureDiscoveryActions.update({
                  payload: { unassignLocationSkipConfirm: true },
                }),
              );
            }
          }),
          map(result => result.confirmed),
        )
      : of(true);
  }

  private openConfirmUnassignLocationDialog$(): Observable<UnassignLocationDialogResult> {
    return this.dialogService
      .openCustom(ConfirmUnassignLocationDialogComponent, { width: DialogWidth.SmallXX })
      .afterClosed.pipe(filterBoolean);
  }
}
